import {action, observable} from 'mobx'

import OfferCustomParam from './OfferCustomParam'
import DeliveryOption from '../delivery/DeliveryOption'
import OfferFieldValueSource from '../../class/value_source/OfferFieldValueSource'
import MarketCoupon from '../../class/promo/MarketCoupon'
import MarketGift from '../../class/promo/MarketGift'
import MarketNPlusM from '../../class/promo/MarketNPlusM'
import MarketDiscount from '../../class/promo/MarketDiscount'
import MarketBonusCardDiscount from '../../class/promo/MarketBonusCardDiscount'
import CatalogSelection from '../catalog_selection/CatalogSelection'

import * as GeneratedDocumentTtlMode from '../../constant/GeneratedDocumentTtlMode'

export default class ProfileSettings {
    @observable id
    @observable name

    @observable document_guid
    @observable document_generated_document_ttl_mode
    @observable document_generated_document_ttl
    /** @type {Boolean} */
    @observable document_generate_by_cron
    /** @type {Boolean} */
    @observable document_allow_generate_on_request

    @observable shop_name
    @observable shop_company
    @observable shop_agency
    @observable shop_email
    /** @type {OfferFieldValueSource} */
    @observable shop_enable_auto_discounts
    @observable shop_is_from_price
    /** @type {OfferFieldValueSource} */
    @observable shop_cpa
    @observable shop_enable_currency
    /** @type {Boolean} */
    @observable shop_timezone
    @observable shop_rfc3339
    @observable shop_rur_enabled
    @observable shop_rur_only

    /** @type {Boolean} */
    @observable delivery_store
    @observable delivery_delivery
    @observable delivery_pickup
    /** @type {Array<DeliveryOption>} */
    @observable delivery_delivery_options
    /** @type {Array<DeliveryOption>} */
    @observable delivery_pickup_options

    /** @type {Boolean} */
    @observable route_is_https
    @observable route_storefront
    @observable route_utm_mark_source
    @observable route_utm_mark_medium
    @observable route_utm_mark_campaign
    @observable route_extra_url_params
    @observable route_image_size

    /** @type {Boolean} */
    @observable category_is_yandex_category_mapping_enabled
    /** @type {Map<int, int>} */
    @observable category_yandex_category_mapping

    @observable catalog_shop_entity_type
    @observable catalog_selection_mode
    @observable catalog_selection_entities_union
    @observable catalog_stocks_selection_mode
    /** @type {Array<String>} */
    @observable catalog_stock_ids
    /** @type {Boolean} */
    @observable catalog_selection_skip_products_with_ignore_param
    /** @type {Boolean} */
    @observable catalog_selection_export_hidden_categories
    @observable catalog_selection_export_collections
    /** @type {Boolean} */
    @observable catalog_selection_export_hidden_products
    /** @type {Boolean} */
    @observable catalog_selection_export_out_of_stock
    /** @type {Boolean} */
    @observable catalog_selection_export_infinity_stock
    /** @type {Boolean} */
    @observable catalog_selection_export_without_images
    @observable catalog_selection_export_skus_without_personal_image
    @observable catalog_selection_min_exported_price
    @observable catalog_selection_min_exported_price_currency
    /** @type {Boolean} */
    @observable catalog_export_separate_skus

    @observable catalog_offer_id_template
    @observable collection_name

    /** @type {Array<OfferCustomParam>} */
    @observable offer_field_mapping_custom_params

    /** @type {Map<int, MarketCoupon>} */
    @observable promo_coupons
    /** @type {Map<String, MarketGift>} */
    @observable promo_gifts
    /** @type {Map<int, MarketNPlusM>} */
    @observable promo_n_plus_m_promos
    /** @type {Map<int, MarketDiscount>} */
    @observable promo_discounts
    /** @type {MarketBonusCardDiscount} */
    @observable promo_bonus_card_discount
    /** @type {OfferFieldValueSource} */
    @observable promo_bonus_card_discount_value_source

    /** @type {CatalogSelection} */
    @observable catalog_selection

    @observable _document_generated_document_ttl

    constructor(profile) {
        if (!profile) {
            return
        }

        this.id = profile.id
        this.name = profile.name

        this.document_guid = profile.document_guid
        this.document_generated_document_ttl_mode = profile.document_generated_document_ttl_mode
        this.document_generated_document_ttl = profile.document_generated_document_ttl
        this.document_generate_by_cron = profile.document_generate_by_cron
        this.document_allow_generate_on_request = profile.document_allow_generate_on_request

        this.shop_name = profile.shop_name
        this.shop_company = profile.shop_company
        this.shop_agency = profile.shop_agency
        this.shop_email = profile.shop_email
        this.shop_enable_auto_discounts = profile.shop_enable_auto_discounts
        this.shop_is_from_price = profile.shop_is_from_price
        this.shop_cpa = profile.shop_cpa
        this.shop_enable_currency = profile.shop_enable_currency
        this.shop_timezone = profile.shop_timezone
        this.shop_rfc3339 = profile.shop_rfc3339
        this.shop_rur_enabled = profile.shop_rur_enabled
        this.shop_rur_only = profile.shop_rur_only

        this.delivery_delivery = profile.delivery_delivery
        this.delivery_store = profile.delivery_store
        this.delivery_delivery_options = profile.delivery_delivery_options.map(option => new DeliveryOption(option))
        this.delivery_pickup_options = profile.delivery_pickup_options.map(option => new DeliveryOption(option))

        this.route_is_https = profile.route_is_https
        this.route_storefront = profile.route_storefront
        this.route_utm_mark_source = profile.route_utm_mark_source
        this.route_utm_mark_medium = profile.route_utm_mark_medium
        this.route_utm_mark_campaign = profile.route_utm_mark_campaign
        this.route_extra_url_params = profile.route_extra_url_params
        this.route_image_size = profile.route_image_size

        this.category_is_yandex_category_mapping_enabled = profile.category_is_yandex_category_mapping_enabled
        this.category_yandex_category_mapping = new Map(profile.category_yandex_category_mapping.map(item => ([
            parseInt(item.key),
            parseInt(item.value)
        ])))

        this.catalog_shop_entity_type = profile.catalog_shop_entity_type
        this.catalog_selection_mode = profile.catalog_selection_mode
        this.catalog_selection_entities_union = profile.catalog_selection_entities_union
        this.catalog_stocks_selection_mode = profile.catalog_stocks_selection_mode
        this.catalog_stock_ids = profile.catalog_stock_ids.map(id => id.toString())
        this.catalog_selection_skip_products_with_ignore_param = profile.catalog_selection_skip_products_with_ignore_param
        this.catalog_selection_export_hidden_categories = profile.catalog_selection_export_hidden_categories
        this.catalog_selection_export_collections = profile.catalog_selection_export_collections
        this.catalog_selection_export_hidden_products = profile.catalog_selection_export_hidden_products
        this.catalog_selection_export_out_of_stock = profile.catalog_selection_export_out_of_stock
        this.catalog_selection_export_infinity_stock = profile.catalog_selection_export_infinity_stock
        this.catalog_selection_export_without_images = profile.catalog_selection_export_without_images
        this.catalog_selection_export_skus_without_personal_image = profile.catalog_selection_export_skus_without_personal_image
        this.catalog_selection_min_exported_price = profile.catalog_selection_min_exported_price
        this.catalog_offer_id_template = profile.catalog_offer_id_template
        this.collection_name = profile.collection_name

        this.offer_field_mapping_custom_params = profile.offer_field_mapping_custom_params.map(custom_param => new OfferCustomParam(custom_param))

        this.catalog_selection_min_exported_price_currency = profile.catalog_selection_min_exported_price_currency
        this.catalog_export_separate_skus = profile.catalog_export_separate_skus

        this.promo_coupons = new Map(profile.promo_coupons.map(c => {
            const coupon = new MarketCoupon(c)

            return [
                coupon.coupon_id,
                coupon
            ]
        }))
        this.promo_gifts = new Map(profile.promo_gifts.map(g => {
            const gift = new MarketGift(g)

            return [
                gift.gift_id,
                gift
            ]
        }))
        this.promo_n_plus_m_promos = new Map(profile.promo_n_plus_m_promos.map(npm => {
            const n_plus_m = new MarketNPlusM(npm)

            return [
                n_plus_m.n_plus_m_id,
                n_plus_m
            ]
        }))
        this.promo_discounts = new Map(profile.promo_discounts.map(d => {
            const discount = new MarketDiscount(d)

            return [
                discount.discount_id,
                discount
            ]
        }))

        this.promo_bonus_card_discount = new MarketBonusCardDiscount(profile.promo_bonus_card_discount)
        this.promo_bonus_card_discount_value_source = new OfferFieldValueSource(profile.promo_bonus_card_discount_value_source)

        this.catalog_selection = new CatalogSelection(this.catalog_selection_mode, this.catalog_selection_entities_union)

        this._document_generated_document_ttl = this.document_generated_document_ttl_mode === GeneratedDocumentTtlMode.SECONDS
            ? this.document_generated_document_ttl_mode + '/' + this.document_generated_document_ttl
            : this.document_generated_document_ttl_mode
    }

    @action changeTtlMode(ttl_mode) {
        const mode_parts = ttl_mode.split('/')

        if (mode_parts[0] === GeneratedDocumentTtlMode.NO_TTL_LIMIT) {
            this.document_generated_document_ttl_mode = GeneratedDocumentTtlMode.NO_TTL_LIMIT
        } else if (mode_parts[0] === GeneratedDocumentTtlMode.SECONDS && parseInt(mode_parts[1]) > 0) {
            this.document_generated_document_ttl_mode = GeneratedDocumentTtlMode.SECONDS
            this.document_generated_document_ttl = mode_parts[1]
        }

        this._document_generated_document_ttl = ttl_mode
    }

    @action toggleShopDelivery(toggle) {
        if (toggle === '0' || toggle === '-1') {
            this.delivery_delivery_options = []
        }

        if (toggle === '1' && this.delivery_delivery_options.length === 0) {
            this.delivery_delivery_options.push(new DeliveryOption())
        }
        this.delivery_delivery = toggle
    }

    @action toggleShopPickup(toggle) {
        if (toggle === '0' || toggle === '-1') {
            this.delivery_pickup_options = []
        }

        if (toggle === '1' && this.delivery_pickup_options.length === 0) {
            this.delivery_pickup_options.push(new DeliveryOption())
        }
        this.delivery_pickup = toggle
    }


    @action addNewSimpleCustomParam(param_params) {
        this.offer_field_mapping_custom_params.push(new OfferCustomParam(param_params))
    }

    /**
     * @param {OfferCustomParam} custom_param_to_delete
     */
    @action deleteSimpleCustomParam(custom_param_to_delete) {
        this.offer_field_mapping_custom_params = this.offer_field_mapping_custom_params
            .filter(custom_param => custom_param._unique_key !== custom_param_to_delete._unique_key)
    }

    /**
     * @param {ShopCoupon} shop_coupon
     * @return {MarketCoupon}
     */
    getMarketCouponConfig(shop_coupon) {
        return this.promo_coupons.has(shop_coupon.id)
            ? this.promo_coupons.get(shop_coupon.id)
            : new MarketCoupon()
    }

    /**
     * @param {ShopGift} shop_gift
     * @return {MarketGift}
     */
    getMarketGiftConfig(shop_gift) {
        return this.promo_gifts.has(shop_gift.id)
            ? this.promo_gifts.get(shop_gift.id)
            : new MarketGift()
    }

    /**
     * @param {ShopNPlusM} shop_n_plus_m
     * @return {MarketNPlusM}
     */
    getMarketNPlusMConfig(shop_n_plus_m) {
        return this.promo_n_plus_m_promos.has(shop_n_plus_m.id)
            ? this.promo_n_plus_m_promos.get(shop_n_plus_m.id)
            : new MarketNPlusM()
    }

    /**
     * @param {ShopDiscount} shop_discount
     * @return {MarketDiscount}
     */
    getMarketDiscountConfig(shop_discount) {
        return this.promo_discounts.has(shop_discount.id)
            ? this.promo_discounts.get(shop_discount.id)
            : new MarketDiscount()
    }

    /**
     * @param {ShopCoupon} shop_coupon
     * @return {boolean}
     */
    isMarketCouponEnabled(shop_coupon) {
        try {
            return this.promo_coupons.get(shop_coupon.id).is_enabled
        } catch (e) {
            return false
        }
    }

    /**
     * @param {ShopCoupon} shop_coupon
     * @param {Boolean} toggle
     */
    @action toggleMarketCoupon(shop_coupon, toggle) {
        if (toggle) {
            if (!this.promo_coupons.has(shop_coupon.id)) {
                const coupon = new MarketCoupon()

                coupon.coupon_id = shop_coupon.id
                coupon.is_enabled = true

                this.promo_coupons.set(shop_coupon.id, coupon)
            }

            this.promo_coupons.get(shop_coupon.id).is_enabled = true
        } else {
            try {
                this.promo_coupons.get(shop_coupon.id).is_enabled = false
            } catch (e) {
            }
        }
    }

    /**
     * @param {ShopGift} shop_gift
     */
    isMarketGiftEnabled(shop_gift) {
        try {
            return this.promo_gifts.get(shop_gift.id).is_enabled
        } catch (e) {
            return false
        }
    }

    /**
     * @param {ShopDiscount} shop_discount
     */
    isMarketDiscountEnabled(shop_discount) {
        try {
            return this.promo_discounts.get(shop_discount.id).is_enabled
        } catch (e) {
            return false
        }
    }

    /**
     * @param {ShopGift} shop_gift
     * @param {Boolean} toggle
     */
    @action toggleMarketGift(shop_gift, toggle) {
        if (toggle) {
            if (!this.promo_gifts.has(shop_gift.id)) {
                const gift = new MarketGift()

                gift.gift_id = shop_gift.id

                this.promo_gifts.set(shop_gift.id, gift)
            }

            this.promo_gifts.get(shop_gift.id).is_enabled = true
        } else {
            try {
                this.promo_gifts.get(shop_gift.id).is_enabled = false
            } catch (e) {
            }
        }
    }


    /**
     * @param {ShopNPlusM} shop_n_plus_m
     * @return {boolean}
     */
    isMarketNPlusMEnabled(shop_n_plus_m) {
        try {
            return this.promo_n_plus_m_promos.get(shop_n_plus_m.id).is_enabled
        } catch (e) {
            return false
        }
    }

    /**
     * @param {ShopNPlusM} shop_n_plus_m
     * @param {Boolean} toggle
     */
    @action toggleMarketNPlusM(shop_n_plus_m, toggle) {
        if (toggle) {
            if (!this.promo_n_plus_m_promos.has(shop_n_plus_m.id)) {
                const market_n_plus_m = new MarketNPlusM()

                market_n_plus_m.n_plus_m_id = shop_n_plus_m.id
                market_n_plus_m.is_enabled = true

                this.promo_n_plus_m_promos.set(shop_n_plus_m.id, market_n_plus_m)
            }

            this.promo_n_plus_m_promos.get(shop_n_plus_m.id).is_enabled = true
        } else {
            try {
                this.promo_n_plus_m_promos.get(shop_n_plus_m.id).is_enabled = false
            } catch (e) {
            }
        }
    }

    /**
     * @param {ShopDiscount} shop_discount
     * @param {Boolean} toggle
     */
    @action toggleMarketDiscount(shop_discount, toggle) {
        if (toggle) {
            if (!this.promo_discounts.has(shop_discount.id)) {
                const market_discount = new MarketDiscount()

                market_discount.discount_id = shop_discount.id
                market_discount.is_enabled = true

                this.promo_discounts.set(shop_discount.id, market_discount)
            }

            this.promo_discounts.get(shop_discount.id).is_enabled = true
        } else {
            try {
                this.promo_discounts.get(shop_discount.id).is_enabled = false
            } catch (e) {
            }
        }
    }
}
